import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { STATUSES, REQUEST_TYPES, LOCATIONS } from "../constants";
import { getRequests } from "../services/api";
import StatusBadge from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, FilePlus2, Gem } from "lucide-react";

function pageTitle(role, location) {
  if (role === "Employee") return "My Requests";
  if (role === "Manager") return `${location} Requests`;
  if (role === "HR") return "HR Approval Queue";
  return "Final Approval Queue";
}

export default function Requests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [loc, setLoc] = useState("all");

  useEffect(() => { getRequests().then(setAll).catch(() => {}); }, []);

  const canFilterLoc = user.role === "HR" || user.role === "Director";

  const rows = useMemo(() => all.filter((r) => {
    if (q && !`${r.requestTitle} ${r.employeeName} ${r.employeeEmail}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (status !== "all" && r.currentStatus !== status) return false;
    if (type !== "all" && r.requestType !== type) return false;
    if (canFilterLoc && loc !== "all" && r.employeeLocation !== loc) return false;
    return true;
  }), [all, q, status, type, loc, canFilterLoc]);

  const title = pageTitle(user.role, user.primaryLocation);
  const sel = "bg-vault-panel border-white/10 text-vault-cream h-11";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-panel/50">Requests</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-vault-panel mt-1">{title}</h1>
        </div>
        <button onClick={() => navigate("/app/request/new")}
          className="inline-flex items-center gap-2 rounded-2xl bg-vault-panel text-vault-cream font-semibold px-5 py-3 hover:bg-vault-panel2 transition-colors">
          <FilePlus2 className="h-4 w-4" /> New Request
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-vault-subtle" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or employee" className="bg-vault-panel border-white/10 text-vault-cream pl-9 h-11" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className={sel}><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className={sel}><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{REQUEST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        {canFilterLoc && (
          <Select value={loc} onValueChange={setLoc}>
            <SelectTrigger className={sel}><SelectValue placeholder="Location" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All locations</SelectItem>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      <div className="rounded-3xl bg-vault-panel p-2 sm:p-4">
        {rows.length === 0 ? (
          <p className="text-sm text-vault-subtle py-12 text-center">No requests match your filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-vault-subtle text-xs uppercase tracking-wider">
                  <th className="py-3 px-3 font-semibold">Title</th>
                  {user.role !== "Employee" && <th className="py-3 px-3 font-semibold">Employee</th>}
                  {user.role !== "Employee" && <th className="py-3 px-3 font-semibold">Location</th>}
                  <th className="py-3 px-3 font-semibold">Type</th>
                  <th className="py-3 px-3 font-semibold">Gems</th>
                  <th className="py-3 px-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => navigate(`/app/requests/${r.id}`)}
                    className="border-t border-white/5 cursor-pointer hover:bg-vault-panel2/50">
                    <td className="py-3.5 px-3 text-vault-cream font-medium">{r.requestTitle}</td>
                    {user.role !== "Employee" && <td className="py-3.5 px-3 text-vault-subtle">{r.employeeName}</td>}
                    {user.role !== "Employee" && <td className="py-3.5 px-3 text-vault-subtle">{r.employeeLocation}</td>}
                    <td className="py-3.5 px-3 text-vault-subtle">{r.requestType}</td>
                    <td className="py-3.5 px-3"><span className="inline-flex items-center gap-1 text-vault-cream"><Gem className="h-3.5 w-3.5 text-vault-accent" />{r.gemsAmount}</span></td>
                    <td className="py-3.5 px-3"><StatusBadge status={r.currentStatus} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
